import { Color, Spacing } from '@persona-id/design-tokens';
import { FC } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { Value } from 'dashboard/components/FormControlPilledInput/types';
import { operatorDisplayValue } from 'dashboard/components/GraphQueryEditor/lib';
import { DatumTypeToken, Graph } from 'dashboard/models/graph';
import {
  Conditional,
  generateVariableMap,
  GraphQuery,
  GraphQueryBody,
  ParameterMap,
  QueryType,
  ValueType,
} from 'dashboard/models/graph-query';
import {
  graphQueryParamsFromGraphQueryTemplate,
  GraphQueryTemplate,
} from 'dashboard/models/graph-query-template';
import { Paragraph } from 'design-system/components/Text';
import { BASE } from 'design-system/variables/spacing';
import { PERSONA_DATUM_TYPE_TO_DATUM_TYPE } from 'lib/constants/graph';
import { ApiModel } from 'lib/interfaces/api';
// -----------------------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------------------

// We declare a minimalist type, because we have many different account status structures
// and we only need a few fields right now.
export interface AccountStatus {
  name: string;
  token: string;
}

// -----------------------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------------------
const Text = styled(Paragraph)`
  color: ${Color.PrimaryBlack};

  display: inline-flex;
  margin-right: ${Spacing[1]};
  margin-left: ${Spacing[1]};
  padding-top: ${Spacing[1]};
`;

const PillContainer = styled.div<{ isVariable: boolean }>`
  display: inline-flex;
  white-space: nowrap;
  overflow: hidden;
  height: 24px;
  border-radius: ${BORDER_RADIUSES.rounded};
  ${({ isVariable }) => css`
    background-color: ${isVariable ? Color.CorePurple200 : Color.CoreSlate200};
  `}

  margin: ${Spacing[1]} 0 0 ${Spacing[1]};
  padding: ${BASE / 4}px ${Spacing[2]};
  gap: ${Spacing[1]};
`;

const VariableName = styled(Paragraph)`
  font-family: Courier New;
  color: ${Color.CorePurple800};
`;

const VariableValue = styled(Paragraph)`
  background-color: ${Color.CoreSlate200};
  padding: 0 ${Spacing[4]};
`;

interface PillProps {
  value: Value | string;
  parameterMap?: ParameterMap;
  valueToLabel?: (arg0: any) => any;
}

const Pill = ({ value, parameterMap = {}, valueToLabel = (s) => s }: PillProps) => {
  if (typeof value === 'string') {
    return (
      <PillContainer isVariable={false}>
        <Paragraph color='primary'>{value}</Paragraph>
      </PillContainer>
    );
  }

  if (Array.isArray(value) || (value.type === ValueType.Range && value.range)) {
    return (
      <div>
        <Pill
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          value={Array.isArray(value) ? value[0] : value.range.lower}
          parameterMap={parameterMap}
          valueToLabel={valueToLabel}
        />
        <Pill
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          value={Array.isArray(value) ? value[1] : value.range.upper}
          parameterMap={parameterMap}
          valueToLabel={valueToLabel}
        />
      </div>
    );
  }

  if (typeof value === 'string') {
    return (
      <PillContainer isVariable={false}>
        <Paragraph color='primary'>{value}</Paragraph>
      </PillContainer>
    );
  } else if (value.variable) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const valueStr = valueToLabel(parameterMap[value.name]);
    return (
      <PillContainer isVariable={true}>
        <VariableName size='sm'>{value.name}</VariableName>
        {valueStr && (
          <VariableValue size='sm' color='primary'>
            {valueStr}
          </VariableValue>
        )}
      </PillContainer>
    );
  } else {
    const valueStr = valueToLabel(value.value || value['raw-value']);
    return (
      <PillContainer isVariable={false}>
        <Paragraph color='primary'>{valueStr || value.value}</Paragraph>
      </PillContainer>
    );
  }
};

const QueryRow = styled.div`
  display: flex;
  flex-wrap: wrap;

  padding: ${Spacing[2]};
  gap: ${Spacing[1]};
`;

const HorizontalLine = styled.div`
  border: 1px solid ${Color.CoreSlate200};
  height: 0px;
  width: 100%;
`;
const HorizontalLinePadding = styled.div`
  padding-left: ${Spacing[2]};
  padding-right: ${Spacing[2]};
`;

const QueryPreview = styled.div`
  border-radius: ${BORDER_RADIUSES.rounded};
  background-color: ${Color.CoreSlate50};
  padding: ${Spacing[2]};
  overflow-x: auto;
  display: grid;
  max-width: 800px;
`;

interface GraphReadOnlyQueryProps {
  accountStatuses: AccountStatus[];
  graph: Graph | undefined;
  query: GraphQuery | GraphQueryTemplate;
}

const GraphReadOnlyQuery: FC<GraphReadOnlyQueryProps> = ({ accountStatuses, graph, query }) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  let params: GraphQueryBody = null;
  if (query.type === ApiModel.GraphQuery) {
    // casting is safe as we return early if `params.type` is not from the expected set
    params = query.attributes.params as GraphQueryBody;
  } else {
    // casting is safe as we return early if `params.type` is not from the expected set
    params = graphQueryParamsFromGraphQueryTemplate(query) as GraphQueryBody;
  }
  if (
    ![QueryType.ByAccount, QueryType.ByCluster, QueryType.NHopsFromAccount].includes(params.type)
  ) {
    // Don't render anything for unsupported query types
    return <></>;
  }

  const parameterMap = generateVariableMap(params.variables);

  const sharedPropertiesConditional = params.conditionals.find(
    (conditional) => conditional.left === 'shared_properties',
  );

  const conditionals = params.conditionals.filter(
    (conditional) => conditional.left !== 'shared_properties',
  );

  const accountStatusTokenToLabel = (token: string): string => {
    const matchingAccountStatus = accountStatuses.find(
      (accountStatus) => token === accountStatus.token,
    );
    if (!matchingAccountStatus) {
      return '';
    }
    return matchingAccountStatus.name;
  };

  const gdtToLabel = (token: string): string => {
    // check graph node configs first
    const config = graph?.attributes?.['node-render-configs']?.find(
      (config) => config['datum-type-token'] === token,
    );
    if (config != null) {
      return config['display-name'];
    }
    // then check default persona datum types, then default to the token itself
    const gqt = PERSONA_DATUM_TYPE_TO_DATUM_TYPE.get(token as DatumTypeToken);
    return gqt ? gqt.name : token;
  };

  // set id so that graphCanvas Events do not trigger when hovering over the component
  return (
    <QueryPreview id='graph-explorer__query-editor-card'>
      {conditionals.map((conditional: Conditional, i: number) => {
        return (
          <QueryRow key={`query_row_${i}`}>
            <Text>{i === 0 ? 'Find' : 'And'}</Text>
            <Pill value={conditional.left} />
            <Pill value={operatorDisplayValue(conditional.operator, conditional.left)} />
            {conditional.right.map((value, j) => {
              return <Pill key={`value_${j}`} value={value as Value} parameterMap={parameterMap} />;
            })}
          </QueryRow>
        );
      })}
      {conditionals.length && (sharedPropertiesConditional || 'num-hops' in params) ? (
        <HorizontalLinePadding>
          <HorizontalLine />
        </HorizontalLinePadding>
      ) : null}
      {sharedPropertiesConditional ? (
        <>
          <QueryRow>
            <Text>Where accounts connect by</Text>
            <Pill
              value={operatorDisplayValue(
                sharedPropertiesConditional.operator,
                'shared_properties',
              )}
            />
            {sharedPropertiesConditional.right.map((value, j) => {
              return (
                <Pill
                  key={`value_${j}`}
                  value={value as Value}
                  parameterMap={parameterMap}
                  valueToLabel={gdtToLabel}
                />
              );
            })}
          </QueryRow>
          {params.type === QueryType.NHopsFromAccount
            ? params['traversal-conditionals']?.map((conditional: Conditional, i: number) => {
                return (
                  <QueryRow key={`traversal_filtering_row_${i}`}>
                    <Text>{i === 0 ? 'With' : 'And'}</Text>
                    <Pill value={conditional.left} />
                    <Pill value={operatorDisplayValue(conditional.operator, conditional.left)} />
                    {conditional.right.map((value, j) => {
                      return (
                        <Pill
                          key={`value_${j}`}
                          value={value as Value}
                          valueToLabel={accountStatusTokenToLabel}
                        />
                      );
                    })}
                  </QueryRow>
                );
              })
            : null}
        </>
      ) : null}
      {'num-hops' in params ? (
        <QueryRow>
          <Text>At most</Text>
          <Pill value={params['num-hops'].toString()} />
          <Text>hops away</Text>
        </QueryRow>
      ) : null}
    </QueryPreview>
  );
};

export default GraphReadOnlyQuery;
